/**
 * TWO LEDGERS OVER ONE BAR STREAM: the algorithm's, and the operator's.
 *
 * <h2>What this is for</h2>
 *
 * The replay already shows what the rules did. The question a discretionary operator actually has
 * is "would I have done better", and the only honest way to answer it is to let them trade the
 * same bars as they arrive and score both books by the same measure.
 *
 * <h2>STRICT DECOUPLING, enforced by shape rather than by discipline</h2>
 *
 * The algo ledger is produced by the backend and replayed; this module never writes to it. Every
 * action here returns a new state whose `algo` is the SAME OBJECT REFERENCE it was given, so the
 * isolation is checkable with `Object.is` rather than by comparing fields and hoping the list was
 * complete. A test asserts exactly that.
 *
 * <p>That matters more than it sounds. The whole experiment is void if a human action can nudge
 * the mechanical baseline — the comparison would be against a moving target, and the direction of
 * the error would be invisible.
 *
 * <h2>ONE POSITION AT A TIME, like the engine it is being compared with</h2>
 *
 * DynaMan holds one premise at a time. A human ledger that could pyramid would be compared
 * against a book that cannot, and the difference would be the position management rather than the
 * judgement. Buy while short reverses; buy while long is a no-op, not an add.
 *
 * <h2>Fills at the replay bar's CLOSE, and then through the BACKEND'S OWN cost model</h2>
 *
 * The close is the only price available without lookahead: the operator is deciding while looking
 * at the bar, and it is the last price that bar can honestly offer.
 *
 * <p>From there the trade is priced by `accountCosts`, which mirrors `AccountSimulation` —
 * direction-aware slippage on each leg, commission on both, and a size derived from a risk per
 * share that includes the cost of being stopped out. That is COST PARITY, and it is the condition
 * on the whole feature: the two columns exist to be subtracted from one another, so charging the
 * algo book 5bps and the human book nothing would make the human look better by exactly the
 * friction it was not charged. 20.7% of the published SPY result is friction; a free human column
 * would be winning by a fifth of the answer before trading at all.
 *
 * <p>`pnlPercent` stays GROSS, on the raw prices, because that is what the engine's own
 * `TradeRecord.pnlPercent` is — `AccountSimulation` never rewrites it. `netPnl` is the figure that
 * carries the costs. Same two definitions on both sides, which is the only thing that makes the
 * comparison a comparison.
 */

import { calculatePortfolioMetrics, institutionalRatios, displayDrawdownPercent }
  from './portfolioMetrics';
import { normaliseAccount, sizeShares, priceTrade } from './accountCosts';

/** How a discretionary position is sized. */
export const SIZING = {
  /**
   * The algo's own dollar allocation at this bar.
   *
   * <p>Read from the replay frame's account state — the shares the engine actually holds. When
   * the engine is FLAT there is nothing to match, so the most recent non-zero allocation is used
   * and the trade records that it was inherited rather than concurrent. Falling back silently to
   * a different sizing model would make two trades in one ledger incomparable with each other.
   */
  MATCH_ALGO: 'MATCH_ALGO',

  /**
   * A fixed fraction of the operator's CURRENT equity, so the book compounds.
   *
   * <p>With a stop supplied, the size is the honest risk calculation: budget over the distance
   * to the stop. WITHOUT one — which a discretionary click usually is — risk cannot be converted
   * to a size at all, and the fraction is applied to NOTIONAL instead. The two are very
   * different and the trade records which was used; see `sizingBasis`.
   */
  FIXED_RISK: 'FIXED_RISK',
};

export const DEFAULT_RISK_PERCENT = 1.0;

/** A ledger with nothing in it. */
export const EMPTY_HUMAN = Object.freeze({
  position: null,
  trades: [],
  /** Why the last action did nothing, or null. Surfaced so a refused click is never silent. */
  lastRefusal: null,
});

const num = (v, fallback = 0) => (Number.isFinite(v) ? v : fallback);

/** The close of the bar an action is taken on. The only price this module ever fills at. */
export function fillPriceOf(bar) {
  if (!bar) return null;
  // `close` first, then the 4-element OHLC array the chart uses — [open, close, low, high].
  if (Number.isFinite(bar.close)) return bar.close;
  if (Array.isArray(bar.ohlc) && Number.isFinite(bar.ohlc[1])) return bar.ohlc[1];
  return null;
}

/**
 * How many shares a discretionary entry gets.
 *
 * @return {{shares: number, basis: string, note: string|null}} — `basis` names which rule
 *         produced the number, because MATCH_ALGO and the two FIXED_RISK readings are not
 *         comparable with one another and a ledger that mixed them without saying so would be
 *         unreadable.
 */
export function sizeFor({
  model, equity, price, stop, riskPercent, algoShares, lastAlgoShares, account, direction,
}) {
  const px = num(price);
  if (px <= 0) return { shares: 0, basis: 'NONE', note: 'no usable fill price' };

  if (model === SIZING.MATCH_ALGO) {
    const live = Math.abs(num(algoShares));
    if (live > 0) return { shares: live, basis: 'MATCH_ALGO', note: null };
    const inherited = Math.abs(num(lastAlgoShares));
    if (inherited > 0) {
      return {
        shares: inherited,
        basis: 'MATCH_ALGO_INHERITED',
        note: 'the algo is flat; sized from its most recent position',
      };
    }
    return { shares: 0, basis: 'NONE', note: 'the algo has never held a position to match' };
  }

  const pct = num(riskPercent, DEFAULT_RISK_PERCENT);
  if (num(equity) * pct <= 0) return { shares: 0, basis: 'NONE', note: 'no equity to risk' };

  // WITH A STOP this goes through the BACKEND'S OWN sizing, so risk per share includes the
  // slippage on both fills and the two commissions. The naive |entry - stop| oversizes by about
  // 1% at the replay's levers and more as the stop tightens — a human book sized that way would
  // report a drawdown larger than the risk setting beside it claims.
  // The ledger's OWN risk percentage and the ROLLING equity, with the cost levers taken from the
  // account the replay was priced with. `startingEquity` is set from the live balance only so that
  // `isSized` is true — the size itself is computed from `equity`, which is what compounds.
  const acct = {
    ...normaliseAccount(account),
    riskPerTradePercent: pct,
    startingEquity: Math.max(num(equity), 1),
  };
  if (Number.isFinite(stop)) {
    const sized = sizeShares({ direction, entryPrice: px, stop, account: acct, equity: num(equity) });
    if (sized.shares > 0) {
      return {
        shares: sized.shares,
        basis: 'FIXED_RISK_STOP',
        note: sized.capped ? 'capped by buying power' : null,
      };
    }
    return { shares: 0, basis: 'NONE', note: sized.skipReason };
  }

  // NO STOP, which is the ordinary discretionary click. Risk cannot be converted to a size at all,
  // so the percentage is applied to NOTIONAL and the trade records that it was — two trades in one
  // ledger sized by different rules are not comparable, and nothing else on screen would say so.
  let shares = num(equity) * pct / 100 / px;
  if (!acct.allowFractionalShares) shares = Math.floor(shares);
  if (!(shares > 0)) {
    return { shares: 0, basis: 'NONE', note: 'one share would exceed the notional budget' };
  }
  return {
    shares,
    basis: 'FIXED_RISK_NOTIONAL',
    note: 'no stop given, so the percentage is of NOTIONAL and not of risk',
  };
}

/** A fresh dual state. `algo` is whatever the replay supplies and is never written here. */
export function initialDualState(options = {}) {
  return {
    algo: options.algo || null,
    human: EMPTY_HUMAN,
    config: {
      sizingModel: options.sizingModel === SIZING.FIXED_RISK
        ? SIZING.FIXED_RISK : SIZING.MATCH_ALGO,
      riskPercent: num(options.riskPercent, DEFAULT_RISK_PERCENT),
      startingEquity: Number.isFinite(options.startingEquity) ? options.startingEquity : null,
      /**
       * THE SAME ACCOUNT THE ALGO RUN WAS PRICED WITH.
       *
       * <p>Absent means frictionless, which is a real configuration — an unsized replay — rather
       * than a defaulted one. It is the caller's job to pass the object the replay request was
       * built from; `DynaManPhase2.test.js` asserts that App does, because a forgotten prop here
       * does not error, it quietly gives the human book free execution.
       */
      account: normaliseAccount(options.account),
    },
  };
}

/** Realised equity: the opening balance plus every closed trade. Open marks are NOT counted. */
export function humanEquity(state) {
  const start = state && state.config ? state.config.startingEquity : null;
  if (!Number.isFinite(start)) return null;
  return start + (state.human.trades || []).reduce((a, t) => a + num(t.netPnl), 0);
}

/**
 * Closes the open position at a bar, returning the completed trade.
 *
 * <p>`pnlPercent` is signed by DIRECTION, so a short that falls is a gain. Getting that backwards
 * would make every short read as its own mirror image, and on a book with both sides the
 * aggregate would look like noise rather than like a sign error.
 */
function closeAt(position, bar, price, reason, account) {
  const exit = num(price);
  const sign = position.direction === 'SHORT' ? -1 : 1;
  // GROSS, on the raw prices, because that is what the engine's own `TradeRecord.pnlPercent` is.
  // `AccountSimulation` never rewrites it, so matching that definition is what lets the two
  // Gross Return columns be held against each other.
  const pnlPercent = ((exit - position.entryPrice) / position.entryPrice) * 100 * sign;
  const priced = priceTrade({
    direction: position.direction,
    entryPrice: position.entryPrice,
    exitPrice: exit,
    shares: position.shares,
    account,
  });
  return {
    sequence: position.sequence,
    direction: position.direction,
    entryTime: position.entryTime,
    entryPrice: position.entryPrice,
    exitTime: (bar && bar.date) || null,
    exitPrice: exit,
    shares: position.shares,
    sizingBasis: position.sizingBasis,
    sizingNote: position.sizingNote,
    pnlPercent,
    // NET OF COSTS, by the backend's own arithmetic. The costs are kept alongside rather than
    // folded away: "the human made 900" and "made 990 and paid 90 to trade" are the same net and
    // different stories, and the second is the one a copilot comparison is about.
    netPnl: priced.net,
    grossPnl: priced.gross,
    entryFill: priced.entryFill,
    exitFill: priced.exitFill,
    commission: priced.commission,
    slippageCost: priced.slippageCost,
    exitReason: reason,
    tradingDays: 1,
    source: 'HUMAN',
  };
}

/** Opens a position at a bar. */
function openAt({ direction, bar, price, shares, basis, note, sequence }) {
  return {
    direction,
    entryTime: (bar && bar.date) || null,
    entryPrice: num(price),
    shares,
    sizingBasis: basis,
    sizingNote: note,
    sequence,
  };
}

/**
 * Records why something the operator tried did nothing, without touching either book.
 *
 * <p>For a refusal the reducer cannot express, because it is about the GESTURE rather than the
 * order: a click on a bar behind the playhead never becomes an action at all, so there is no
 * action to refuse. It still has to be said — a click that does nothing and says nothing is
 * indistinguishable from a broken chart, which is the rule every refusal in this module follows.
 *
 * <p>The algo half is carried through BY REFERENCE, as on every other path here, so the isolation
 * guarantee is checkable with `Object.is` rather than by comparing fields and hoping the list was
 * complete.
 */
export function noteRefusal(state, why) {
  const s = state || initialDualState();
  return { algo: s.algo, config: s.config, human: { ...s.human, lastRefusal: why } };
}

/**
 * The one reducer every human action goes through.
 *
 * @param state  the dual state
 * @param action {@code {type: 'BUY'|'SELL'|'FLATTEN', bar, stop?, algoShares?, lastAlgoShares?}}
 * @return a NEW state whose `algo` is the same reference it was given
 */
export function dualLedgerReducer(state, action) {
  const s = state || initialDualState();
  const { type, bar } = action || {};
  const price = fillPriceOf(bar);

  // THE ALGO HALF IS CARRIED THROUGH BY REFERENCE, always and on every path including the
  // refusals. This is the isolation guarantee, and it is a property of this one line.
  const keep = (human) => ({ algo: s.algo, config: s.config, human });

  const refuse = (why) => keep({ ...s.human, lastRefusal: why });

  if (type === 'FLATTEN') {
    if (!s.human.position) return refuse('nothing open to flatten');
    if (price === null) return refuse('this bar has no usable close');
    return keep({
      position: null,
      trades: [
        ...s.human.trades,
        closeAt(s.human.position, bar, price, 'Flattened', s.config.account),
      ],
      lastRefusal: null,
    });
  }

  if (type !== 'BUY' && type !== 'SELL') return refuse(`unknown action ${String(type)}`);
  if (price === null) return refuse('this bar has no usable close');

  const wanted = type === 'BUY' ? 'LONG' : 'SHORT';

  // ALREADY THERE IS A NO-OP, not an add. One position at a time, matching the engine this book
  // is being compared against -- otherwise the difference between them would be partly the
  // position management rather than the judgement.
  if (s.human.position && s.human.position.direction === wanted) {
    return refuse(`already ${wanted.toLowerCase()}`);
  }

  const equity = humanEquity(s);
  const { shares, basis, note } = sizeFor({
    model: s.config.sizingModel,
    equity,
    price,
    stop: action.stop,
    riskPercent: s.config.riskPercent,
    algoShares: action.algoShares,
    lastAlgoShares: action.lastAlgoShares,
    account: s.config.account,
    direction: wanted,
  });
  if (!(shares > 0)) return refuse(note || 'position would be zero-sized');

  // A REVERSAL CLOSES FIRST, and the closed trade is kept. Dropping it would lose a real result
  // and make the ledger's trade count disagree with the actions taken.
  const trades = s.human.position
    ? [...s.human.trades,
      closeAt(s.human.position, bar, price, `Reversed to ${wanted}`, s.config.account)]
    : s.human.trades;

  return keep({
    position: openAt({
      direction: wanted, bar, price, shares, basis, note, sequence: trades.length + 1,
    }),
    trades,
    lastRefusal: null,
  });
}

/** Convenience wrappers, so a component never builds an action object by hand. */
export const executeBuy = (state, bar, extra = {}) =>
  dualLedgerReducer(state, { type: 'BUY', bar, ...extra });
export const executeSell = (state, bar, extra = {}) =>
  dualLedgerReducer(state, { type: 'SELL', bar, ...extra });
export const flattenPosition = (state, bar) =>
  dualLedgerReducer(state, { type: 'FLATTEN', bar });

/**
 * Both books scored by the SAME measure.
 *
 * <p>Both sides go through `calculatePortfolioMetrics` and `institutionalRatios` — the point of
 * the comparison is that nothing differs except the trades. Scoring the human side with its own
 * arithmetic is precisely the defect the shared reducer was introduced to end.
 *
 * <p>The OPEN position is excluded from both, as it is everywhere else here: its number is a
 * mark, not a result, and on this rule set a single open trade can be worth more than every
 * closed one together.
 */
export function compareLedgers(state, algoTrades, options = {}) {
  const s = state || initialDualState();
  const capital = s.config.startingEquity;

  const score = (trades) => {
    const base = calculatePortfolioMetrics(trades, capital);
    const ratios = institutionalRatios(trades, {
      riskFreeRate: options.riskFreeRate,
      benchmarkReturns: options.benchmarkReturns,
      // The drawdown Calmar divides by is the one the panel renders, so the two cannot disagree.
      maxDrawdownPercent: displayDrawdownPercent(base),
    });
    return { ...base, ...ratios, displayDrawdownPercent: displayDrawdownPercent(base) };
  };

  return {
    algo: score(Array.isArray(algoTrades) ? algoTrades : []),
    human: score(s.human.trades),
    openHumanPosition: s.human.position,
    sizingModel: s.config.sizingModel,
  };
}
